/**
 * RefLink — resolve a foreign-key value (`*_user_id` / `*_id`) to a
 * human-readable, clickable inline link (design-system §5.2 / frontend-display
 * §3.4).
 *
 * Every `*_id` value on a show page is a `RefLink`: a *quiet* link
 * (`text-primary hover:underline`), not a button, not raw text. It resolves the
 * target's display label via the Refine `useOne` hook (the boxed dataProvider →
 * `GET /api/{resource}/:id`) and falls back gracefully:
 *
 *   - empty id        → a muted em-dash (`—`)
 *   - while resolving → the mono id (no layout jump, no spinner)
 *   - resolve fails   → the mono id, still clickable (degrade, never crash)
 *
 * The label is read defensively from the resolved record by trying a list of
 * common title-ish columns; callers may override with an explicit `labelField`
 * or a ready `label`. Resolution can be disabled with `resolve={false}` for
 * hot list cells where N extra fetches aren't worth it.
 */
import * as React from "react";
import { useOne, useGo, type BaseRecord } from "@refinedev/core";

import { cn } from "@/lib/utils";

/** Columns tried, in order, to derive a human label from a resolved record. */
const LABEL_FIELDS = [
  "title",
  "name",
  "label",
  "display_name",
  "full_name",
  "summary",
  "subject",
  "event_type",
  "key",
  "slug",
] as const;

function deriveLabel(
  record: BaseRecord | undefined,
  labelField: string | undefined,
): string | undefined {
  if (!record) return undefined;
  if (labelField) {
    const v = record[labelField];
    if (typeof v === "string" && v.trim() !== "") return v;
    if (typeof v === "number") return String(v);
  }
  for (const field of LABEL_FIELDS) {
    const v = record[field];
    if (typeof v === "string" && v.trim() !== "") return v;
  }
  return undefined;
}

export interface RefLinkProps {
  /** Target resource path (Refine `name`, e.g. `"artifacts"`, `"users"`). */
  resource: string;
  /** Foreign-key value to resolve + link to. */
  id: string | number | null | undefined;
  /** Pre-resolved display label; skips the lookup when provided. */
  label?: React.ReactNode;
  /** Record column to read the label from (overrides the default heuristics). */
  labelField?: string;
  /** Resolve the human label via `useOne`. Default `true`. */
  resolve?: boolean;
  className?: string;
}

/**
 * Quiet inline link to another entity's show page, resolving a readable label.
 */
export function RefLink({
  resource,
  id,
  label,
  labelField,
  resolve = true,
  className,
}: RefLinkProps) {
  const go = useGo();
  const idStr = id === null || id === undefined ? "" : String(id);
  const hasId = idStr !== "";

  // Only resolve when we have an id, no explicit label, and resolution is on.
  const shouldResolve = hasId && resolve && label === undefined;
  const { data } = useOne<BaseRecord>({
    resource,
    id: idStr,
    queryOptions: {
      enabled: shouldResolve,
      retry: false,
    },
    // A missing/forbidden ref target must never surface a toast — an unresolved
    // ref degrades silently to the mono id (degrade, never crash).
    successNotification: false,
    errorNotification: false,
  });

  if (!hasId) {
    return <span className="text-muted-foreground">—</span>;
  }

  const resolved = label ?? deriveLabel(data?.data, labelField);
  const text = resolved ?? idStr;
  const isId = resolved === undefined;

  return (
    <a
      href="#"
      onClick={(e) => {
        e.preventDefault();
        go({ to: { resource, id: idStr, action: "show" } });
      }}
      className={cn(
        "cursor-pointer text-primary hover:underline focus-visible:underline",
        // Unresolved values fall back to the mono id so it still reads as a ref.
        isId && "font-mono text-xs",
        className,
      )}
      title={isId ? undefined : idStr}
    >
      {text}
    </a>
  );
}

export default RefLink;
